import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ActivityResult, CategoryCount } from '@services/activity.service';
import {
  ALL_FILTER,
  RESULT_OPTIONS,
  fromSelectValue,
  toSelectValue,
} from './auditFilterOptions';

interface AuditFiltersProps {
  resultFilter: '' | ActivityResult;
  categoryFilter: string;
  categories: CategoryCount[];
  canExport: boolean;
  exporting: boolean;
  onResultChange: (result: '' | ActivityResult) => void;
  onCategoryChange: (category: string) => void;
  onExportCsv: () => void;
}

const AuditFilters: React.FC<AuditFiltersProps> = ({
  resultFilter,
  categoryFilter,
  categories,
  canExport,
  exporting,
  onResultChange,
  onCategoryChange,
  onExportCsv,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-3">
      <h6 className="revy-cjk text-base font-medium leading-[1.5]">{t('audit.recentEvents')}</h6>
      <div className="flex-1" />

      <Select
        value={toSelectValue(resultFilter)}
        onValueChange={(next) => onResultChange(fromSelectValue(next) as '' | ActivityResult)}
      >
        <SelectTrigger aria-label={t('audit.result')} className="w-auto min-w-[7rem] gap-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="revy-ui">
          {RESULT_OPTIONS.map((option) => (
            <SelectItem key={option || 'all'} value={toSelectValue(option)} className="revy-cjk">
              {t(`audit.results.${option || 'all'}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={toSelectValue(categoryFilter)}
        onValueChange={(next) => onCategoryChange(fromSelectValue(next))}
      >
        <SelectTrigger aria-label={t('audit.category')} className="w-auto min-w-[150px] gap-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="revy-ui">
          <SelectItem value={ALL_FILTER} className="revy-cjk">
            {t('audit.categoryAll')}
          </SelectItem>
          {categories.map((category) => (
            <SelectItem
              key={category.action_category}
              value={category.action_category}
              className="revy-cjk"
            >
              {category.action_category}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Hidden rather than disabled: the export endpoint is admin-only and
          403s for a manager, so there is nothing for them to press. */}
      {canExport && (
        <Button variant="outline" onClick={onExportCsv} disabled={exporting}>
          <Download strokeWidth={1.9} />
          CSV
        </Button>
      )}
    </div>
  );
};

export default AuditFilters;
